import { useState, useMemo } from "react";
import { Link } from "react-router-dom";
import { usePublishedPackages } from "@/hooks/usePublicPackage";
import { useEventTypes } from "@/hooks/usePackages";
import { usePricingTypes, getUnitLabel } from "@/hooks/usePricingTypes";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { PublicNav } from "@/components/website/PublicNav";
import { PublicFooter } from "@/components/website/PublicFooter";
import { PublicLayout } from "@/components/website/PublicLayout";
import { HeroImage } from "@/components/website/HeroImage";
import { useDocumentMeta } from "@/hooks/useDocumentMeta";
import { publicPackagesBrowseUrl, publicBaseUrl } from "@/lib/public-urls";
import { ogImageUrl, packagesItemListJsonLd } from "@/lib/seo";
import { Skeleton } from "@/components/ui/skeleton";
import { Users, PackageOpen, ArrowRight, AlertTriangle } from "lucide-react";

/** Placeholder card shown while packages are loading — matches real card dimensions. */
function PackageCardSkeleton() {
  return (
    <div className="rounded-lg border bg-card overflow-hidden h-full">
      {/* Cover image placeholder */}
      <Skeleton className="h-48 w-full rounded-none" />
      <div className="p-6 space-y-3">
        {/* Title + arrow row */}
        <div className="flex items-start justify-between gap-2">
          <Skeleton className="h-6 w-3/4" />
          <Skeleton className="h-5 w-5 shrink-0" />
        </div>
        {/* Description */}
        <Skeleton className="h-4 w-full" />
        <Skeleton className="h-4 w-2/3" />
        {/* Price / guests row */}
        <div className="flex items-center gap-3">
          <Skeleton className="h-4 w-20" />
          <Skeleton className="h-4 w-16" />
        </div>
        {/* Event type badges */}
        <div className="flex gap-1 pt-2 border-t border-border">
          <Skeleton className="h-5 w-16 rounded-full" />
          <Skeleton className="h-5 w-20 rounded-full" />
        </div>
      </div>
    </div>
  );
}

export default function PublicPackagesBrowsePage() {
  const { data: packages, isLoading, error, refetch, isFetching } = usePublishedPackages();
  const { data: eventTypes } = useEventTypes();
  const { data: pricingTypes } = usePricingTypes();
  const [eventFilter, setEventFilter] = useState<string>("all");

  const filtered = useMemo(() => {
    if (!packages) return [];
    if (eventFilter === "all") return packages;
    return packages.filter((pkg) =>
      pkg.event_types?.some((et) => et.event_type_id === eventFilter)
    );
  }, [packages, eventFilter]);

  const firstCover = useMemo(() => {
    const first = packages?.find((p) => p.cover_image_url);
    return ogImageUrl(first?.og_image_url ?? first?.cover_image_url);
  }, [packages]);

  const seoJsonLd = useMemo(() => {
    if (!packages?.length) return undefined;
    return packagesItemListJsonLd(packages, publicBaseUrl());
  }, [packages]);

  useDocumentMeta({
    title: "Catering Packages — Tristano's Catering",
    description: "Browse our curated catering packages for weddings, corporate events, and private parties.",
    canonical: publicPackagesBrowseUrl(),
    ogImage: firstCover,
    ogType: "website",
    twitterCard: "summary_large_image",
    jsonLd: seoJsonLd,
  });

  // Get active event types that have at least one published package
  const activeEventTypes = useMemo(() => {
    if (!eventTypes || !packages) return [];
    const usedIds = new Set(packages.flatMap((p) => p.event_types?.map((et) => et.event_type_id) || []));
    return eventTypes.filter((et) => usedIds.has(et.id));
  }, [eventTypes, packages]);

  return (
    <PublicLayout className="min-h-screen bg-background">
      <PublicNav />

      <main id="main-content" tabIndex={-1} className="focus:outline-none">
      {/* Hero */}
      <div className="bg-primary py-16 md:py-24">
        <div className="max-w-5xl mx-auto px-4 text-center">
          <h1 className="text-3xl md:text-5xl font-heading font-bold text-primary-foreground mb-4">
            Our Catering Packages
          </h1>
          <p className="text-primary-foreground/80 max-w-2xl mx-auto text-base md:text-lg font-secondary">
            Explore our curated packages designed for every occasion. Each package can be customized to fit your event.
          </p>
        </div>
      </div>

      {/* Filters */}
      {activeEventTypes.length > 0 && (
        <div className="max-w-5xl mx-auto px-4 py-6">
          <div className="flex flex-wrap gap-2 justify-center">
            <Button
              variant={eventFilter === "all" ? "default" : "outline"}
              size="sm"
              onClick={() => setEventFilter("all")}
            >
              All Events
            </Button>
            {activeEventTypes.map((et) => (
              <Button
                key={et.id}
                variant={eventFilter === et.id ? "default" : "outline"}
                size="sm"
                onClick={() => setEventFilter(et.id)}
              >
                {et.name}
              </Button>
            ))}
          </div>
        </div>
      )}

      {/* Packages Grid */}
      <div className="max-w-5xl mx-auto px-4 py-8 pb-16">
        {isLoading ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {Array.from({ length: 6 }).map((_, i) => (
              <PackageCardSkeleton key={i} />
            ))}
          </div>
        ) : error ? (
          <div className="text-center py-16">
            <AlertTriangle className="h-12 w-12 text-muted-foreground/40 mx-auto mb-4" />
            <h2 className="text-xl font-heading font-semibold text-foreground mb-2">
              We're having trouble loading this page
            </h2>
            <p className="text-muted-foreground mb-6">Please try again in a moment.</p>
            <Button
              onClick={() => refetch()}
              disabled={isFetching}
              className="min-h-[44px]"
            >
              {isFetching ? "Retrying…" : "Try again"}
            </Button>
          </div>
        ) : filtered.length === 0 ? (
          <div className="text-center py-16">
            <PackageOpen className="h-12 w-12 text-muted-foreground/30 mx-auto mb-4" />
            <h2 className="text-xl font-heading font-semibold text-foreground mb-2">No Packages Available</h2>
            <p className="text-muted-foreground">Check back soon for our latest offerings.</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {filtered.map((pkg) => {
              const unitLabel = getUnitLabel(pricingTypes, pkg.pricing_type);
              const isCustomerChoice = pkg.selection_mode === "customer_choice";
              const totalSelections = isCustomerChoice
                ? (pkg.category_slots || []).reduce((sum, s) => sum + s.quantity, 0)
                : 0;

              return (
                <Link key={pkg.id} to={`/packages/${pkg.slug}`}>
                  <Card className="group hover:shadow-lg transition-all duration-300 overflow-hidden h-full">
                    {pkg.cover_image_url && (
                      <div className="relative h-48 w-full overflow-hidden">
                        <HeroImage
                          src={pkg.cover_image_url}
                          alt={pkg.name}
                          // Cards sit in a 1/2/3-column grid (md:grid-cols-2 lg:grid-cols-3),
                          // so a 100vw hero variant would be badly over-fetched.
                          sizes="(min-width: 1024px) 33vw, (min-width: 768px) 50vw, 100vw"
                        />
                      </div>
                    )}
                    <CardContent className={`p-6 space-y-3 ${!pkg.cover_image_url ? "pt-6" : ""}`}>
                      <div className="flex items-start justify-between gap-2">
                        <h3 className="font-heading font-semibold text-lg text-foreground group-hover:text-primary transition-colors">
                          {pkg.name}
                        </h3>
                        <ArrowRight className="h-5 w-5 text-muted-foreground group-hover:text-primary transition-colors shrink-0 mt-0.5" />
                      </div>

                      {pkg.description && (
                        <p className="text-sm text-muted-foreground line-clamp-2 font-secondary">{pkg.description}</p>
                      )}

                      <div className="flex items-center gap-3 text-sm">
                        {pkg.show_prices && (
                          <span className="font-semibold text-foreground">
                            ${Number(pkg.price).toFixed(2)}
                            <span className="text-muted-foreground font-normal">/{unitLabel}</span>
                          </span>
                        )}
                        {(pkg.min_guests || pkg.max_guests) && (
                          <span className="flex items-center gap-1 text-muted-foreground">
                            <Users className="h-3.5 w-3.5" />
                            {pkg.min_guests && pkg.max_guests
                              ? `${pkg.min_guests}–${pkg.max_guests}`
                              : pkg.min_guests
                              ? `${pkg.min_guests}+`
                              : `≤${pkg.max_guests}`}
                          </span>
                        )}
                      </div>

                      {isCustomerChoice && (
                        <Badge variant="secondary" className="text-xs">
                          {totalSelections} selections · Customer Choice
                        </Badge>
                      )}

                      {(pkg.event_types?.length ?? 0) > 0 && (
                        <div className="flex flex-wrap gap-1 pt-2 border-t border-border">
                          {pkg.event_types!.map((et) => (
                            <Badge key={et.id} variant="outline" className="text-xs font-normal">
                              {et.event_type?.name}
                            </Badge>
                          ))}
                        </div>
                      )}
                    </CardContent>
                  </Card>
                </Link>
              );
            })}
          </div>
        )}
      </div>
      </main>

      <PublicFooter />
    </PublicLayout>
  );
}
